import { http } from './http';
import { tokensFromResponse, type Tokens } from '@/auth/tokenStore';
import { normPasskey, type Passkey } from './types';
import type { LoginOptionsWire, RegisterOptionsWire } from '@/auth/webauthn';

type R = Record<string, unknown>;

export const authApi = {
  /** Google/Apple sign-in and sign-up (same call). */
  async oauth(provider: 'google' | 'apple', idToken: string): Promise<Tokens> {
    const json = await http.post<R>('/auth/oauth', { provider, id_token: idToken, platform: 'web' }, { anonymous: true });
    return tokensFromResponse(json);
  },

  /**
   * Demo sign-in for app-store reviewers: email + a shared secret, no passkey and no Google/Apple
   * account. Returns the same body as /auth/oauth, so tokens, refresh and logout all follow the
   * paths that already exist.
   *
   * The secret is NEVER stored in this app — the reviewer types it. Nothing here identifies the
   * demo account either; the caller decides when to offer this, so the flow is not discoverable by
   * reading the bundle.
   */
  async demo(email: string, secret: string): Promise<Tokens> {
    const json = await http.post<R>('/auth/demo', { email, secret, platform: 'web' }, { anonymous: true });
    return tokensFromResponse(json, email);
  },

  sendEmailCode: (email: string) => http.post<R>('/auth/email/code', { email }, { anonymous: true }),

  async verifyEmailCode(email: string, code: string): Promise<string> {
    const json = await http.post<R>('/auth/email/verify', { email, code }, { anonymous: true });
    return String(json.verification_token ?? '');
  },

  passkeyRegisterOptions: (body: { email: string; device_name: string; verification_token?: string }, authenticated: boolean) =>
    http.post<RegisterOptionsWire>('/auth/passkey/register/options', body, { anonymous: !authenticated }),

  async passkeyRegisterVerify(body: R, authenticated: boolean): Promise<Tokens | null> {
    const json = await http.post<R>('/auth/passkey/register/verify', body, { anonymous: !authenticated });
    return json && json.access_token ? tokensFromResponse(json, body.email as string) : null;
  },

  passkeyLoginOptions: (email?: string) => http.post<LoginOptionsWire>('/auth/passkey/login/options', email ? { email } : {}, { anonymous: true }),

  async passkeyLoginVerify(body: R): Promise<Tokens> {
    const json = await http.post<R>('/auth/passkey/login/verify', body, { anonymous: true });
    return tokensFromResponse(json);
  },

  async listPasskeys(): Promise<Passkey[]> {
    const json = await http.get<unknown>('/auth/passkey/credentials');
    const rows = Array.isArray(json) ? json : ((json as R)?.data as unknown[]) ?? [];
    return (rows as R[]).map(normPasskey);
  },
  revokePasskey: (id: number | string) => http.delete<unknown>(`/auth/passkey/credentials/${id}`),

  session: () => http.get<R>('/auth/session'),
  logout: (refreshToken: string) => http.post<unknown>('/auth/logout', { refresh_token: refreshToken }),
};
